<template>
  <div class="editable-table-container">
    <div>
      <el-table ref="tableRef" v-bind="$attrs" v-on="$listeners" :data="currentPageData" :border="border || true"
        :row-key="rowIdKey" :select-on-indeterminate="false" style="width: 100%"
        @selection-change="handleSelectionChange" header-cell-class-name="custom-table-header"
        :row-style="{ height: '48px' }" @sort-change="handleSortChange" @filter-change="handleFilterChange"
        :tree-props="treeProps" :default-expand-all="defaultExpandAll" :expand-row-keys="localExpandRowKeys"
        @expand-change="handleExpandChange">
        <!-- 透传所有插槽给 el-table -->
        <template v-for="(_, name) in $slots" #[name]>
          <slot :name="name"></slot>
        </template>
        <template v-for="(_, name) in $scopedSlots" #[name]="slotData">
          <slot :name="name" v-bind="slotData"></slot>
        </template>
        <render-table-column v-for="(col, index) in getRenderColumns" :key="col.prop || col.label || `col-${index}`"
          :column="col" :column-index="index" :global-editable="editable" :editing-cells="editingCells"
          :row-id-key="rowIdKey" :current-editing-old-value="currentEditingOldValue" @cell-click="handleCellClick"
          @cell-edit-start="handleCellEditStart" @cell-edit-confirm="handleCellEditConfirm"
          @cell-edit-cancel="handleCellEditCancel" @operate-btn-click="handleOperateBtnClick"
          @edit-cancel-confirm="handleEditCancelConfirm" :is-editing="!!Object.keys(editingCells).length"
          :is-tree-table="isTreeTable" :tree-children-key="treeChildrenKey" :tree-indent="treeIndent"
          :local-expand-row-keys="localExpandRowKeys" :table-data="currentPageData">
        </render-table-column>
      </el-table>
    </div>
    <div v-if="useInternalPagination" class="table-pagination" style="margin-top: 10px; text-align: right;">
      <el-pagination :current-page="currentPage" :page-size="pageSize" :page-sizes="[20, 30, 50, 100, 300]"
        :total="total" layout="total, sizes, prev, pager, next, jumper" @size-change="handlePageSizeChange"
        @current-change="handleCurrentPageChange" />
    </div>
  </div>
</template>

<script>
const RenderTableColumn = {
  name: 'RenderTableColumn',
  props: {
    column: { type: Object, required: true },
    columnIndex: { type: Number, default: 0 },
    globalEditable: { type: Boolean, default: false },
    editingCells: { type: Object, default: () => ({}) },
    rowIdKey: { type: String, default: 'id' },
    currentEditingOldValue: { type: [String, Number, Boolean, null], default: null },
    isEditing: { type: Boolean, default: false },
    isTreeTable: { type: Boolean, default: false },
    treeChildrenKey: { type: String, default: 'children' },
    treeIndent: { type: Number, default: 16 },
    localExpandRowKeys: { type: Array, default: () => [] },
    tableData: { type: Array, default: () => [] }
  },
  methods: {
    getRowKey(row) {
      return row[this.rowIdKey] || JSON.stringify(row);
    },
    isCellEditing(row, prop) {
      const rowKey = this.getRowKey(row);
      return this.editingCells[rowKey]?.[prop] === true;
    },
    isColumnEditable() {
      if (this.column.notEditable) return false;
      return this.column.editable !== undefined ? this.column.editable : this.globalEditable;
    },
    triggerCellClick(row, prop) {
      if (!this.isColumnEditable()) return;
      this.$emit('cell-click', row, prop, row[prop]);
    },
    triggerEditEvent(type, row, prop, value, oldValue) {
      if (!this.isColumnEditable()) return;
      this.$emit(`cell-edit-${type}`, { row, prop, value, oldValue });
    },
    async triggerEditCancelConfirm(row, prop, currentValue, oldValue) {
      if (!this.isColumnEditable()) return false;
      const resolve = await new Promise((resolve) => {
        this.$emit('edit-cancel-confirm', { row, prop, currentValue, oldValue }, resolve);
      });
      if (resolve) {
        this.triggerEditEvent('cancel', row, prop);
      }
      return resolve;
    },
    // 安全的树形层级计算（不依赖scope.table）
    getTreeLevel(row) {
      let level = 0;
      // 方案1：优先使用row自带的level字段（推荐，由父组件处理层级）
      if (row.level !== undefined) {
        return row.level;
      }

      // 方案2：通过parentId递归计算（兼容无level字段场景）
      if (row.parentId && this.tableData.length) {
        let currentParentId = row.parentId;
        while (currentParentId) {
          level++;
          const parentRow = this.tableData.find(item => item[this.rowIdKey] === currentParentId);
          if (!parentRow) break;
          currentParentId = parentRow.parentId;
        }
      }
      return level;
    },
    hasChildren(row) {
      const children = row[this.treeChildrenKey];
      return this.isTreeTable && Array.isArray(children) && children.length > 0;
    },
    isRowExpanded(row) {
      const rowKey = row[this.rowIdKey];
      return this.localExpandRowKeys.includes(rowKey);
    },
    // 获取第一个非系统列（selection/index/operation）的索引
    getFirstContentColumnIndex() {
      // 修复：从父组件columns获取，而非tableData
      const contentColumns = this.$parent.columns?.filter(col =>
        !['selection', 'index', 'operation'].includes(col.type)
      ) || [];
      return contentColumns.length > 0 ? contentColumns.findIndex(col => col.prop === this.column.prop) : 0;
    }
  },
  render(h) {
    const { column, columnIndex, isEditing, isTreeTable, treeIndent, localExpandRowKeys, tableData } = this;
    const canEdit = this.isColumnEditable();
    const align = column.align || 'center';
    const alignStyle = {
      textAlign: align,
      justifyContent: align === 'center' ? 'center' : 'flex-start'
    };

    const columnProps = {
      label: column.label,
      prop: column.prop,
      width: column.width,
      minWidth: column.minWidth,
      fixed: column.fixed,
      resizable: column.resizable ?? true,
      sortable: column.sortable ?? false,
      sortMethod: column.sortMethod,
      sortBy: column.sortBy,
      sortOrders: column.sortOrders || ['ascending', 'descending', null],
      type: column.type,
      index: column.index,
      formatter: column.formatter,
      showOverflowTooltip: column.showOverflowTooltip ?? false
    };

    if (Array.isArray(column.filters) && column.filters.length > 0) {
      Object.assign(columnProps, {
        filters: column.filters,
        filterMethod: column.filterMethod,
        filterMultiple: column.filterMultiple ?? true,
        filteredValue: column.filteredValue
      });
    }

    if (isEditing) {
      columnProps.sortable = false;
      delete columnProps.filters;
    }

    // 选择列
    if (column.type === 'selection') {
      return h('el-table-column', {
        props: { ...columnProps, type: 'selection' },
        attrs: { align: 'center' }
      });
    }

    // 序号列
    if (column.type === 'index') {
      return h('el-table-column', {
        props: { ...columnProps, type: 'index', label: column.label || '序号' },
        attrs: { align: align || 'center' },
        scopedSlots: {
          default: (scope) => {
            return h('div', {
              style: {
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
                padding: '4px 0',
                justifyContent: alignStyle.justifyContent,
                margin: '0 auto'
              }
            }, [
              // 序号列支持自定义插槽
              this.$parent.$scopedSlots[`cell-${column.prop || 'index'}`] ?
                this.$parent.$scopedSlots[`cell-${column.prop || 'index'}`]({ row: scope.row, index: scope.$index }) :
                h('span', scope.$index + 1)
            ]);
          }
        }
      });
    }

    // 多级表头
    if (column.children && column.children.length) {
      return h('el-table-column', {
        props: { ...columnProps },
        attrs: { align: align }
      },
        this.column.children.map((childCol, idx) =>
          h('render-table-column', {
            key: childCol.prop || `child-col-${idx}`,
            props: {
              column: childCol,
              columnIndex: idx,
              globalEditable: this.globalEditable,
              editingCells: this.editingCells,
              rowIdKey: this.rowIdKey,
              currentEditingOldValue: this.currentEditingOldValue,
              isEditing: this.isEditing,
              isTreeTable: this.isTreeTable,
              treeChildrenKey: this.treeChildrenKey,
              treeIndent: this.treeIndent,
              localExpandRowKeys: this.localExpandRowKeys,
              tableData: this.tableData
            },
            on: {
              'cell-click': this.triggerCellClick,
              'cell-edit-start': (data) => this.triggerEditEvent('start', data.row, data.prop, data.value, data.oldValue),
              'cell-edit-confirm': (data) => this.triggerEditEvent('confirm', data.row, data.prop, data.value, data.oldValue),
              'cell-edit-cancel': (data) => this.triggerEditEvent('cancel', data.row, data.prop),
              'operate-btn-click': (data) => this.$emit('operate-btn-click', data),
              'edit-cancel-confirm': this.triggerEditCancelConfirm
            }
          })
        )
      );
    }

    // 操作列
    if (column.type === 'operation') {
      return h('el-table-column', {
        props: { ...columnProps },
        attrs: { align: align },
        scopedSlots: {
          default: (scope) => {
            return h('div', {
              style: {
                display: 'flex',
                gap: '15px',
                alignItems: 'center',
                padding: '4px 0',
                justifyContent: alignStyle.justifyContent,
                margin: '0 auto'
              }
            }, [
              // 核心优化：读取父组件的操作列插槽
              this.$parent.$scopedSlots[`cell-${column.prop || 'operation'}`] ?
                this.$parent.$scopedSlots[`cell-${column.prop || 'operation'}`]({ row: scope.row, index: scope.$index }) :
                column.buttons?.map((btn, btnIdx) => {
                  // 判断是否隐藏按钮
                  const shouldHide = typeof btn.isShow === 'function' ? btn.isShow(scope.row) : btn.isShow;
                  return shouldHide ? '' : h('el-link', {
                    key: btnIdx,
                    props: {
                      type: btn.type || 'primary',
                      icon: btn.icon,
                      disabled: typeof btn.disabled === 'function' ? btn.disabled(scope.row) : btn.disabled || false
                    },
                    style: {
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      textDecoration: 'none'
                    },
                    on: {
                      click: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.$emit('operate-btn-click', { btn, row: scope.row });
                      }
                    }
                  }, [btn.text]);
                })
            ]);
          }
        }
      });
    }

    // 普通列（核心优化：插槽读取父组件的scopedSlots）
    return h('el-table-column', {
      props: { ...columnProps },
      attrs: { align: align },
      scopedSlots: {
        // 表头插槽 - 支持筛选功能
        header: (scope) => {
          const hasFilter = column.filterable && column.filterOpenCallback;
          return h('div', {
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: alignStyle.justifyContent,
              gap: '4px',
              width: '100%'
            }
          }, [
            // 原有表头内容插槽
            this.$parent.$scopedSlots[`header-${column.prop || column.label}`] ?
              this.$parent.$scopedSlots[`header-${column.prop || column.label}`]({ column: scope.column, $index: scope.$index }) :
              h('span', column.label),
            // 筛选图标 - 仅当有 filterable 和 filterOpenCallback 时显示
            hasFilter ? h('el-popover', {
              props: {
                placement: 'bottom-start',
                trigger: 'click',
                popperClass: 'aj-table-custom-filter-popover'
              },
              on: {
                show: () => {
                  // 筛选下拉框展开时触发回调
                  if (typeof column.filterOpenCallback === 'function') {
                    column.filterOpenCallback(column.prop, column.label);
                  }
                }
              }
            }, [
              h('div', {
                slot: 'reference',
                class: 'filter-trigger-icon',
                on: {
                  click: (e) => {
                    e.stopPropagation();
                  }
                }
              }, [
                // 使用 SVG 图标替代 Element UI 图标
                h('svg', {
                  attrs: {
                    viewBox: '0 0 1024 1024',
                    width: '14',
                    height: '14'
                  },
                  style: {
                    fill: column.filters && column.filters.length > 0 ? '#409EFF' : '#c0c4cc'
                  }
                }, [
                  h('path', {
                    attrs: {
                      d: 'M417.6 145.066667a42.666667 42.666667 0 0 1 41.386667 30.72l161.493333 498.346666a42.666667 42.666667 0 0 1-38.186667 55.466667H440.106667a42.666667 42.666667 0 0 1-38.186667-55.466667l161.493333-498.346666a42.666667 42.666667 0 0 1 41.386667-30.72z m0 42.666666a10.666667 10.666667 0 0 0-10.333334 7.68L246.186667 693.013333a10.666667 10.666667 0 0 0 9.493334 13.653334h513.28a10.666667 10.666667 0 0 0 9.493334-13.653334L427.989333 195.413333a10.666667 10.666667 0 0 0-10.333333-7.68z'
                    }
                  })
                ])
              ]),
              // 筛选内容插槽 - 父组件提供的内容
              this.$parent.$scopedSlots[`filter-${column.prop}`] ?
                this.$parent.$scopedSlots[`filter-${column.prop}`]({ column: scope.column, $index: scope.$index }) :
                h('div', { class: 'filter-empty-tip' }, '暂无筛选数据')
            ]) : null
          ]);
        },
        // 单元格插槽
        default: (scope) => {
          const row = scope.row;
          const prop = column.prop;
          const isEditingCell = this.isCellEditing(row, prop);
          const oldValue = this.currentEditingOldValue;
          const currentValue = row[prop];

          // 树形行内前缀（缩进+展开图标）
          let treePrefix = null;
          const isFirstContentColumn = !['selection', 'index', 'operation'].includes(column.type) &&
            columnIndex === this.getFirstContentColumnIndex();

          if (isTreeTable && isFirstContentColumn) {
            const level = this.getTreeLevel(row);
            const indentWidth = level * treeIndent;

            treePrefix = h('span', {
              style: {
                display: 'inline-flex',
                alignItems: 'center',
                marginRight: '4px',
                paddingLeft: `${indentWidth}px`
              }
            }, [
              // 展开/折叠图标
              this.hasChildren(row) ? h('i', {
                class: [
                  'el-icon-arrow-right',
                  this.isRowExpanded(row) ? 'el-table__expand-icon--expanded' : ''
                ],
                style: {
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#409eff',
                  transition: 'transform 0.2s ease',
                  marginRight: '4px',
                  display: 'inline-block'
                },
                on: {
                  click: (e) => {
                    e.stopPropagation();
                    this.$emit('toggle-expand', row, !this.isRowExpanded(row));
                  }
                }
              }) : h('span', { style: { width: '16px', display: 'inline-block' } })
            ]);
          }

          // 编辑态内容（支持自定义编辑插槽）
          const getEditContent = () => {
            // 核心优化：读取父组件的编辑态插槽
            const editSlot = this.$parent.$scopedSlots[`edit-cell-${prop}`];
            if (editSlot) {
              return editSlot({ row, prop, value: currentValue, oldValue });
            }

            // 默认编辑组件
            return h('div', {
              class: 'edit-cell-content',
              style: {
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                alignItems: align,
              },
              on: {
                click: (e) => e.stopPropagation()
              }
            }, [
              h('div', { style: { display: 'flex', alignItems: 'center', width: '100%' } }, [
                treePrefix,
                h('el-input', {
                  props: {
                    value: currentValue,
                    size: 'mini',
                    placeholder: `请输入${column.label}`
                  },
                  attrs: { 'data-edit-cell': `${this.getRowKey(row)}-${prop}` },
                  style: { flex: 1 },
                  on: {
                    input: (val) => this.$set(row, prop, val),
                    'keyup.enter': () => {
                      this.triggerEditEvent('confirm', row, prop, row[prop], oldValue);
                    }
                  },
                  ref: 'editInput',
                  hook: {
                    mounted: () => {
                      this.$nextTick(() => {
                        const input = this.$refs.editInput;
                        if (input) {
                          input.focus();
                          input.select();
                        }
                      });
                    }
                  }
                })
              ]),
              h('div', {
                style: {
                  display: 'flex',
                  gap: '8px',
                  justifyContent: align === 'center' ? 'center' : 'flex-start',
                }
              }, [
                h('el-button', {
                  props: { size: 'mini', type: 'text', plain: true },
                  style: { color: '#409eff', padding: '2px 3px', height: '24px', lineHeight: '24px' },
                  on: {
                    click: (e) => {
                      e.stopPropagation();
                      this.triggerEditEvent('confirm', row, prop, row[prop], oldValue);
                    }
                  }
                }, ['确认']),
                h('el-button', {
                  props: { size: 'mini', type: 'text', plain: true },
                  style: { color: '#f56c6c', padding: '2px 3px', height: '24px', lineHeight: '24px' },
                  on: {
                    click: async (e) => {
                      e.stopPropagation();
                      if (currentValue === oldValue) {
                        this.triggerEditEvent('cancel', row, prop);
                        return;
                      }
                      await this.triggerEditCancelConfirm(row, prop, currentValue, oldValue);
                    }
                  }
                }, ['取消'])
              ])
            ]);
          };

          // 非编辑态内容（核心优化：读取父组件的普通插槽）
          const getNormalContent = () => {
            // 获取列配置
            const showTooltip = column.showTooltip !== false; // 默认开启tooltip
            const tooltipField = column.tooltipField || prop; // tooltip显示的字段，默认当前单元格
            // 修改为：
            const tooltipContent = row[tooltipField] != null ? String(row[tooltipField]) : '';

            const lines = column.lines || 3; // 默认显示3行
            const cellPadding = column.cellPadding !== undefined ? column.cellPadding : 5; // 默认5px内边距

            // 空值处理：显示空字符串
            const displayValue = (row[prop] === undefined || row[prop] === null ? '' : String(row[prop]));

            // 内容组件
            let contentComponent;
            if (this.$parent.$scopedSlots[`cell-${prop}`]) {
              contentComponent = this.$parent.$scopedSlots[`cell-${prop}`]({ row, column, index: scope.$index });
            } else {
              contentComponent = displayValue;
            }

            // 单元格内容容器 - 强制截断和多行显示
            const cellContentStyle = {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: lines,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-all',
              width: '100%',
              lineHeight: '1.5'
            };

            // 普通单元格内容
            const cellContent = h('div', { style: cellContentStyle }, contentComponent);

            // tooltip 处理
            if (showTooltip && tooltipContent) {
              return h('el-tooltip', {
                props: {
                  effect: column.tooltipEffect || 'dark',
                  placement: column.tooltipPlacement || 'top',
                  enterable: column.tooltipEnterable !== false,
                  content: tooltipContent,
                  openDelay: column.tooltipOpenDelay !== undefined ? column.tooltipOpenDelay : 100, // 默认100ms显示
                  visibleArrow: true,
                  popperOptions: { boundariesElement: 'viewport' },
                  popperClass: 'aj-table-tooltip-popper'
                },
                style: { display: 'inline-block', width: '100%', height: '100%' }
              }, [h('div', { style: { width: '100%', height: '100%' } }, [cellContent])]);
            }

            return cellContent;
          };

          // 单元格容器 - 使用配置的 padding
          const cellContainerPadding = column.cellPadding !== undefined ? column.cellPadding : 5;
          const cellContainerProps = {
            style: {
              cursor: canEdit ? 'pointer' : 'text',
              padding: `${cellContainerPadding}px`,
              userSelect: canEdit ? 'none' : 'text',
              textAlign: align,
              width: '100%',
              boxSizing: 'border-box'
            },
            on: {}
          };

          if (canEdit) {
            cellContainerProps.on.click = (e) => {
              e.stopPropagation();
              this.triggerCellClick(row, prop);
            };
          }

          return h('div', cellContainerProps, [
            isEditingCell ? getEditContent() : getNormalContent()
          ]);
        }
      }
    });
  }
};

export default {
  name: 'ajTable',
  components: { RenderTableColumn },
  expose: ['clearEditingState', 'forceCloseEditState', 'expandAll', 'collapseAll', 'clearSelection'],
  props: {
    data: { type: Array, required: true, default: () => [] },
    columns: { type: Array, required: true, default: () => [] },
    editable: { type: Boolean, default: false },
    useInternalPagination: { type: Boolean, default: false },
    currentPage: { type: Number, default: 1 },
    pageSize: { type: Number, default: 1 },
    total: { type: Number, default: 0 },
    rowIdKey: { type: String, default: 'id' },
    border: { type: Boolean, default: true },
    visibleColumns: { type: Object, default: () => ({}) },
    cancelEditTip: { type: String, default: '当前单元格内容已修改，是否确认放弃修改？' },
    isTreeTable: { type: Boolean, default: false },
    treeChildrenKey: { type: String, default: 'children' },
    defaultExpandAll: { type: Boolean, default: false },
    expandRowKeys: { type: Array, default: () => [] },
    treeIndent: { type: Number, default: 16 }
  },
  data() {
    return {
      editingCells: Object.create(null),
      currentEditingOldValue: null,
      currentEditingCell: { rowKey: null, prop: null },
      localCheckedIds: [],
      isShowingCancelConfirm: false,
      eventLock: false,
      localExpandRowKeys: [...this.expandRowKeys],
      isPaging: false, // 标记是否是翻页操作
      lastRestoreSelectionIds: null // 记录上次恢复的选中ID，防止重复恢复
    };
  },
  computed: {
    currentPageData() {
      if (!Array.isArray(this.data) || this.data.length === 0) return [];
      return this.data
    },
    getRenderColumns() {
      if (!Array.isArray(this.columns) || this.columns.length === 0) return [];
      this.initVisibleColumns();
      const filterCols = (cols) => {
        return cols.map(col => {
          if (!col) return null;
          if (col.children && Array.isArray(col.children)) {
            const filteredChildren = filterCols(col.children).filter(Boolean);
            return { ...col, children: filteredChildren };
          }
          const key = col.prop || col.label;
          return this.visibleColumns[key] === false ? null : col;
        }).filter(Boolean);
      };
      return filterCols([...this.columns]);
    },
    localVisibleColumns() {
      return { ...this.visibleColumns };
    },
    treeProps() {
      return {
        children: this.treeChildrenKey,
        hasChildren: (row) => {
          const children = row[this.treeChildrenKey];
          return Array.isArray(children) && children.length > 0;
        },
        indent: 0 // 关闭默认换行缩进
      };
    }
  },
  watch: {
    expandRowKeys: {
      immediate: true,
      handler(newVal) {
        this.localExpandRowKeys = [...newVal];
      }
    },
    defaultExpandAll: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.isTreeTable) {
          this.expandAll();
        }
      }
    },
    currentPageData: {
      immediate: true,
      handler(newVal) {
        if (!this.isTreeTable) return;
        // 初始化行的展开状态标记
        newVal.forEach(row => {
          row.expanded = this.localExpandRowKeys.includes(row[this.rowIdKey]);
        });
      }
    }
  },
  methods: {
    initVisibleColumns() {
      const init = (cols) => {
        if (!Array.isArray(cols)) return;
        cols.forEach(col => {
          if (!col) return;
          const key = col.prop || col.label;
          if (this.visibleColumns[key] === undefined) {
            this.$set(this.visibleColumns, key, true);
          }
          if (col.children) init(col.children);
        });
      };
      init(this.columns);
      this.$emit('update:visibleColumns', this.localVisibleColumns);
    },
    handleSelectionChange(selRows) {
      if (!Array.isArray(selRows)) return;
      const currentIds = this.currentPageData.map(row => row[this.rowIdKey]);

      // 翻页时跳过处理，由翻页方法控制恢复选中状态
      if (this.isPaging) return;

      // 检查是否是重复触发（用于防止 restoreSelection 循环）
      const currentSelectIds = selRows.map(row => row[this.rowIdKey]).sort();
      if (this.lastRestoreSelectionIds &&
          JSON.stringify(this.lastRestoreSelectionIds) === JSON.stringify(currentSelectIds)) {
        return;
      }

      // 非翻页时，正常处理选中状态
      this.localCheckedIds = this.localCheckedIds.filter(id => !currentIds.includes(id));
      this.localCheckedIds = [...new Set([...this.localCheckedIds, ...selRows.map(row => row[this.rowIdKey])])];
      this.$emit('update:checkedIds', this.localCheckedIds);
      this.$emit('selection-change', selRows);
    },
    handlePageSizeChange(val) {
      this.isPaging = true;
      this.lastRestoreSelectionIds = null;
      this.$emit('update:pageSize', val);
      this.$emit('page-change', { currentPage: this.currentPage, pageSize: val });
      // 延迟恢复选中状态
      setTimeout(() => {
        this.restoreSelection();
        this.isPaging = false;
      }, 50);
    },
    handleCurrentPageChange(val) {
      this.isPaging = true;
      this.lastRestoreSelectionIds = null;
      this.$emit('update:currentPage', val);
      this.$emit('page-change', { currentPage: val, pageSize: this.pageSize });
      // 延迟恢复选中状态
      setTimeout(() => {
        this.restoreSelection();
        this.isPaging = false;
      }, 50);
    },
    handleSortChange(sort) {
      this.$emit('sort-change', sort);
    },
    handleFilterChange(filters) {
      this.$emit('filter-change', filters);
    },
    handleCellClick(row, prop, oldValue) {
      const targetColumn = this.getRenderColumns.find(col => col.prop === prop);
      const columnCanEdit = targetColumn?.editable ?? this.editable;
      if (!columnCanEdit) return;

      if (this.eventLock || this.isShowingCancelConfirm) return;

      if (this.currentEditingCell.rowKey) {
        this.handleCancelEditing(true);
        return;
      }

      const rowKey = row[this.rowIdKey] || JSON.stringify(row);
      this.clearEditingState();
      this.$set(this.editingCells, rowKey, { [prop]: true });
      this.currentEditingCell = { rowKey, prop };
      this.currentEditingOldValue = oldValue;
      this.$emit('cell-edit-start', { row, prop, oldValue });
    },
    handleCellEditStart(data) {
      const targetColumn = this.getRenderColumns.find(col => col.prop === data.prop);
      const columnCanEdit = targetColumn?.editable ?? this.editable;
      if (!columnCanEdit) return;
      this.$emit('cell-edit-start', data);
    },
    handleCellEditConfirm(data) {
      const targetColumn = this.getRenderColumns.find(col => col.prop === data.prop);
      const columnCanEdit = targetColumn?.editable ?? this.editable;
      if (!columnCanEdit || this.eventLock) return;

      this.eventLock = true;

      const { row, prop, value, oldValue } = data;
      this.forceCloseEditState();

      if (value !== oldValue) {
        this.$set(row, prop, value);
        this.$emit('cell-edit-confirm', data);
      }

      this.$nextTick(() => {
        this.eventLock = false;
      });
    },
    handleCellEditCancel(data) {
      const targetColumn = this.getRenderColumns.find(col => col.prop === data.prop);
      const columnCanEdit = targetColumn?.editable ?? this.editable;
      if (!columnCanEdit || this.eventLock) return;

      this.handleCancelEditing(false);
      this.$emit('cell-edit-cancel', data);

      this.$nextTick(() => {
        this.eventLock = false;
      });
    },
    handleEditCancelConfirm({ row, prop, currentValue, oldValue }, resolve) {
      const targetColumn = this.getRenderColumns.find(col => col.prop === prop);
      const columnCanEdit = targetColumn?.editable ?? this.editable;
      if (!columnCanEdit) return resolve(false);

      if (this.isShowingCancelConfirm || this.eventLock) return resolve(false);

      this.isShowingCancelConfirm = true;
      this.eventLock = true;

      this.$confirm(this.cancelEditTip, '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
        beforeClose: (action, instance, done) => {
          instance.$el.addEventListener('click', (e) => e.stopPropagation(), { once: true });
          done();
        }
      }).then(() => {
        this.$set(row, prop, oldValue);
        this.clearEditingState();
        resolve(true);
      }).catch(() => {
        resolve(false);
      }).finally(() => {
        this.isShowingCancelConfirm = false;
        this.eventLock = false;
      });
    },
    handleCancelEditing(isClickNewCell = false) {
      if (!this.currentEditingCell.rowKey || !this.currentEditingCell.prop) return;
      if (this.eventLock || this.isShowingCancelConfirm) return;

      const { rowKey, prop } = this.currentEditingCell;
      const currentRow = this.currentPageData.find(row => (row[this.rowIdKey] || JSON.stringify(row)) === rowKey);
      if (!currentRow) {
        this.clearEditingState();
        return;
      }

      const currentValue = currentRow[prop];
      const oldValue = this.currentEditingOldValue;

      if (currentValue === oldValue) {
        this.clearEditingState();
        return;
      }

      this.eventLock = true;

      const confirmCallback = () => {
        this.$set(currentRow, prop, oldValue);
        this.clearEditingState();
        this.eventLock = false;
      };

      const cancelCallback = () => {
        this.eventLock = false;
        return;
      };

      this.$confirm(this.cancelEditTip, '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
        beforeClose: (action, instance, done) => {
          instance.$el.addEventListener('click', (e) => e.stopPropagation(), { once: true });
          done();
        }
      }).then(confirmCallback).catch(cancelCallback);
    },
    clearEditingState() {
      this.editingCells = Object.create(null);
      this.currentEditingCell = { rowKey: null, prop: null };
      this.currentEditingOldValue = null;
      this.isShowingCancelConfirm = false;
      this.eventLock = false;
      this.$nextTick(() => {
        if (this.$refs.tableRef) {
          this.$refs.tableRef.doLayout();
        }
      });
    },
    forceCloseEditState() {
      const { rowKey, prop } = this.currentEditingCell;
      if (rowKey && prop && this.editingCells[rowKey]) {
        this.$delete(this.editingCells[rowKey], prop);
        if (Object.keys(this.editingCells[rowKey]).length === 0) {
          this.$delete(this.editingCells, rowKey);
        }
      }

      this.currentEditingCell = { rowKey: null, prop: null };
      this.currentEditingOldValue = null;
      this.isShowingCancelConfirm = false;
      this.eventLock = false;

      this.$nextTick(() => {
        if (this.$refs.tableRef) {
          this.$refs.tableRef.doLayout();
          this.$forceUpdate();
        }
      });
    },
    handleOperateBtnClick(data) {
      this.$emit('operate-btn-click', data);
    },
    handleGlobalClick(e) {
      // 如果是复制操作（Ctrl+C 或 Cmd+C），忽略
      if (e.ctrlKey || e.metaKey) return;

      if (!Object.keys(this.editingCells).length) return;

      // 如果点击的是文本选择区域，允许复制
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;

      const isEditArea = e.target.closest('.edit-cell-content') || e.target.closest('[data-edit-cell]');
      const isDialogArea = e.target.closest('.el-message-box') || e.target.closest('.el-dialog');
      if (isEditArea || isDialogArea || this.eventLock || this.isShowingCancelConfirm) return;

      const isTableArea = e.target.closest('.editable-table-container');
      if (!isTableArea) {
        this.handleCancelEditing();
      }
    },
    // 处理展开/折叠事件
    handleExpandChange(row, expanded) {
      const rowKey = row[this.rowIdKey];
      if (expanded) {
        if (!this.localExpandRowKeys.includes(rowKey)) {
          this.localExpandRowKeys.push(rowKey);
        }
      } else {
        this.localExpandRowKeys = this.localExpandRowKeys.filter(id => id !== rowKey);
      }
      row.expanded = expanded;
      this.$emit('update:expandRowKeys', [...this.localExpandRowKeys]);
      this.$emit('expand-change', row, expanded);
    },
    // 展开所有行
    expandAll() {
      if (!this.isTreeTable || !this.currentPageData.length) return;
      const getAllRowIds = (data) => {
        let ids = [];
        data.forEach(row => {
          ids.push(row[this.rowIdKey]);
          row.expanded = true;
          const children = row[this.treeChildrenKey];
          if (Array.isArray(children) && children.length) {
            ids = [...ids, ...getAllRowIds(children)];
          }
        });
        return ids;
      };
      this.localExpandRowKeys = getAllRowIds(this.currentPageData);
      this.$emit('update:expandRowKeys', [...this.localExpandRowKeys]);
    },
    // 折叠所有行
    collapseAll() {
      if (!this.isTreeTable) return;
      const collapseAllRows = (data) => {
        data.forEach(row => {
          row.expanded = false;
          const children = row[this.treeChildrenKey];
          if (Array.isArray(children) && children.length) {
            collapseAllRows(children);
          }
        });
      };
      collapseAllRows(this.currentPageData);
      this.localExpandRowKeys = [];
      this.$emit('update:expandRowKeys', []);
    },
    // 处理列组件触发的展开/折叠
    handleToggleExpand(row, expanded) {
      this.handleExpandChange(row, expanded);
    },
    // 清空表格选中状态
    clearSelection() {
      if (this.$refs.tableRef) {
        this.$refs.tableRef.clearSelection();
      }
      this.localCheckedIds = [];
      this.$emit('update:checkedIds', []);
    },
    // 恢复当前页的选中状态
    restoreSelection() {
      if (!this.$refs.tableRef) return;
      const currentIds = this.currentPageData.map(row => row[this.rowIdKey]);
      const idsToSelect = currentIds.filter(id => this.localCheckedIds.includes(id));

      if (idsToSelect.length > 0) {
        const rowsToSelect = this.currentPageData.filter(row => idsToSelect.includes(row[this.rowIdKey]));
        this.$refs.tableRef.clearSelection();
        // 批量设置选中状态，避免多次触发 selection-change
        if (rowsToSelect.length > 0) {
          this.$refs.tableRef.toggleRowSelection(rowsToSelect[0], true);
          for (let i = 1; i < rowsToSelect.length; i++) {
            this.$refs.tableRef.toggleRowSelection(rowsToSelect[i], true);
          }
        }
        // 记录本次恢复的选中ID，防止 handleSelectionChange 重复处理
        this.lastRestoreSelectionIds = idsToSelect.sort();
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleGlobalClick, { capture: true, passive: true });
    this.$nextTick(() => {
      if (this.$refs.tableRef) {
        this.$refs.tableRef.doLayout();
        // 初始化时恢复选中状态
        if (this.localCheckedIds && this.localCheckedIds.length > 0) {
          this.restoreSelection();
        }
      }
    });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleGlobalClick, { capture: true });
    this.clearEditingState();
  }
};
</script>

<style scoped>
.editable-table-container {
  width: 100%;
}

::v-deep .custom-table-header {
  background-color: #f5f7fa !important;
  color: #333 !important;
  font-weight: 500 !important;
  padding-left: 12px !important;
}

::v-deep .el-table td,
::v-deep .el-table th {
  padding: 0 !important;
  border-color: #e6e8eb !important;
}

::v-deep .el-table--border {
  border-color: #e6e8eb !important;
}

::v-deep .el-table--border th,
::v-deep .el-table--border td {
  border-right-color: #e6e8eb !important;
}

::v-deep .el-table .el-table-column--selection .cell {
  position: relative !important;
  overflow: visible !important;
}

::v-deep .el-table .el-table-column--selection .cell::before,
::v-deep .el-table .el-table-column--selection .cell::after {
  content: none !important;
}

::v-deep .el-table .el-table-column--selection {
  width: 48px !important;
  text-align: center !important;
}

/* 树形样式优化 - 确保同行显示 */
::v-deep .el-table .el-table__indent {
  display: none;
}

::v-deep .el-table .el-table__expand-icon {
  display: inline-block !important;
  margin-right: 4px !important;
  vertical-align: middle !important;
}

::v-deep .el-icon-arrow-right.el-table__expand-icon--expanded {
  transform: rotate(90deg);
}

::v-deep .el-table td .cell {
  width: 100% !important;
  padding: 0 !important;
}

.el-table td>div {
  width: 100%;
}

/* 自定义筛选图标样式 */
.filter-trigger-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-trigger-icon:hover {
  background-color: #f0f0f0;
}

.filter-trigger-icon .el-icon-filter {
  font-size: 14px;
  color: #c0c4cc;
  transition: color 0.2s;
}

.filter-trigger-icon .el-icon-filter:hover {
  color: #409EFF;
}

.filter-trigger-icon .el-icon-filter.filter-active-custom {
  color: #409EFF;
}

/* 自定义筛选弹窗样式 */
.aj-table-custom-filter-popover {
  min-width: 180px;
  max-width: 200px;
  padding: 0 !important;
}

.aj-table-custom-filter-popover .filter-empty-tip {
  padding: 12px 16px;
  color: #909399;
  font-size: 12px;
  text-align: center;
}

/* 筛选内容样式 */
.filter-content {
  max-height: 220px;
  overflow-y: auto;
  padding: 8px;
}

.filter-content .el-checkbox {
  display: block;
  margin-right: 0;
  margin-bottom: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.filter-content .el-checkbox:hover {
  background-color: #f5f7fa;
}

.filter-content .el-checkbox:last-child {
  margin-bottom: 0;
}

.filter-content .el-checkbox__label {
  font-size: 12px;
  color: #606266;
}

.el-input--mini .el-input__inner {
  padding: 2px 8px;
  height: 28px;
  line-height: 28px;
}

::v-deep .el-link {
  text-decoration: none;
  color: #409eff !important;
  font-size: 12px !important;
  margin: 0 4px;
}

::v-deep .el-link+.el-link {
  margin-left: 8px;
}

::v-deep .el-link:hover {
  color: #66b1ff !important;
}

.table-pagination {
  margin-top: 10px;
  text-align: right;
}

::v-deep .el-button--text:hover {
  background-color: #f5f7fa;
  border-radius: 4px;
}

::v-deep .edit-cell-content {
  padding: 2px 0;
  width: 100%;
}

/* Tooltip 样式 - 限制最大宽度为200px */
.aj-table-tooltip-popper {
  max-width: 200px !important;
  word-break: break-all;
  line-height: 1.5;
}

.aj-table-tooltip-popper.is-dark {
  background: rgba(0, 0, 0, 0.9) !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  font-size: 12px !important;
}

.aj-table-tooltip-popper.is-light {
  background: white !important;
  border: 1px solid #e5e7eb !important;
  color: #374151 !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  font-size: 12px !important;
}
</style>

<style>
/* 全局 tooltip 样式 - 限制最大宽度为200px */
.aj-table-tooltip-popper {
  max-width: 200px !important;
  word-break: break-all;
  line-height: 1.5;
  font-size: 12px;
}

/* 深色主题 tooltip */
.aj-table-tooltip-popper.is-dark {
  background: rgba(0, 0, 0, 0.9) !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  font-size: 12px !important;
}

/* 浅色主题 tooltip */
.aj-table-tooltip-popper.is-light {
  background: white !important;
  border: 1px solid #e5e7eb !important;
  color: #374151 !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  font-size: 12px !important;
}

/* 防止 tooltip 导致单元格变大 */
.el-tooltip {
  width: 100% !important;
  height: 100% !important;
  display: block !important;
}

.el-tooltip .el-tooltip__rel {
  width: 100% !important;
  height: 100% !important;
}
</style>